/**
 * Deck view (overlay) — inspect the current run's full deck + owned Programs.
 * Read-only; opened from anywhere via the topbar / V key. Sorted by cost then
 * name for a tidy, scannable list. Also peeks combat piles when in a fight.
 */

import { el, div, button } from "../dom";
import { cardFace } from "../widgets";
import { getCardDef, getProgramDef } from "../../engine";
import type { Store } from "../store";
import type { CardInstance } from "../../engine/types";

type DeckTab = "deck" | "draw" | "discard" | "exhaust";
let tab: DeckTab = "deck";

export function resetDeckTab(): void {
  tab = "deck";
}

export function renderDeck(store: Store): HTMLElement {
  const run = store.run!;
  const inCombat = run.phase === "combat" && run.combat != null;

  const overlay = div("overlay");
  const modal = div("modal");
  modal.style.maxWidth = "900px";
  modal.style.height = "92%";

  // Header.
  modal.appendChild(
    div("row", [
      el("div", { class: "h-title", text: "LOADOUT" }),
      div("fill"),
      button("btn btn--sm", "✕ CLOSE", () => store.closeOverlay()),
    ]),
  );

  // Programs strip.
  if (run.programs.length > 0) {
    const progs = div("row wrap");
    for (const pid of run.programs) {
      const p = getProgramDef(pid);
      progs.appendChild(
        el("div", {
          class: "pip buff",
          title: p.description,
          text: `◆ ${p.name}`,
          style: "padding:4px 6px;font-size:11px;white-space:normal",
        }),
      );
    }
    modal.appendChild(div("panel", [
      el("div", { class: "muted", text: "PROGRAMS", style: "margin-bottom:4px" }),
      progs,
    ]));
  }

  // Tabs (piles only meaningful in combat).
  if (inCombat) {
    const tabs = div("tabs");
    const mk = (t: DeckTab, label: string, n: number) =>
      button(`tab${tab === t ? " active" : ""}`, `${label} (${n})`, () => {
        tab = t;
        store.requestRender();
      });
    const c = run.combat!;
    tabs.appendChild(mk("deck", "DECK", run.deck.length));
    tabs.appendChild(mk("draw", "DRAW", c.drawPile.length));
    tabs.appendChild(mk("discard", "DISCARD", c.discardPile.length));
    tabs.appendChild(mk("exhaust", "EXHAUST", c.exhaustPile.length));
    modal.appendChild(tabs);
  } else {
    tab = "deck";
  }

  // Card grid.
  const cards = pileFor(store, tab);
  const grid = div("row wrap scroll");
  grid.style.flex = "1 1 auto";
  grid.style.alignContent = "flex-start";
  grid.style.justifyContent = "center";
  if (cards.length === 0) {
    grid.appendChild(el("div", { class: "muted", text: "— empty —" }));
  } else {
    for (const card of sortCards(cards)) {
      grid.appendChild(
        cardFace(card.defId, { playable: false }),
      );
    }
  }
  modal.appendChild(grid);

  modal.appendChild(
    el("div", {
      class: "muted center",
      text: `${run.deck.length} cards · ${run.programs.length} programs`,
    }),
  );

  overlay.appendChild(modal);
  // Click outside to close.
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) store.closeOverlay();
  });
  return overlay;
}

function pileFor(store: Store, t: DeckTab): CardInstance[] {
  const run = store.run!;
  if (t === "deck" || !run.combat) return run.deck;
  if (t === "draw") return run.combat.drawPile;
  if (t === "discard") return run.combat.discardPile;
  return run.combat.exhaustPile;
}

function sortCards(cards: CardInstance[]): CardInstance[] {
  return [...cards].sort((a, b) => {
    const da = getCardDef(a.defId);
    const db = getCardDef(b.defId);
    if (da.cost !== db.cost) return da.cost - db.cost;
    return da.name.localeCompare(db.name);
  });
}
